import { BUTTON_SIZE, BUTTON_TYPE, DSButton, useToast } from '@repo/design-system/index'
import { QIcon_chevron_left } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import { QueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import apis from '../../../modules/apis'
import { enterConversationAi } from '../../../modules/apis/student/conversation'
import { axiosGet } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_speaking, n_upcoming_lessons } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { MaterialSubTab, TextProvider, fetchMaterial } from '../../../pages/[lang]/portal/materials/detail/[materialId]'
import { useAITutorStack } from '../../../pages/[lang]/webview/ai-tutor'
import MaterialBodyComponent from '../../new-portal/materials/MaterialBodyComponent'
import MaterialMobileBottomTab from '../../new-portal/materials/MaterialMobileBottomTab'
import WebviewStackContainer from '../common/WebviewStackContainer'

interface IMaterialDetail {
  params: {
    materialId: string
  }
}

export const AITutorDiscussionMaterialDetailBar = ({ pop, materialId, isConversationAvailable }) => {
  const { t } = useTranslation()

  const { useAITutorFlow } = useAITutorStack()
  const { push } = useAITutorFlow()
  const router = useAppRouter({ stackPush: push, browserDetect: true })
  const { toastError, toastSuccess } = useToast()
  const [loading, setLoading] = useState(false)

  const getFullUrl = useReturnFullUrl()

  const onStartConversation = async (courseId) => {
    if (loading) return
    setLoading(true)
    await enterConversationAi({ courseId }, router, getFullUrl, true)
    setLoading(false)
  }

  const handleSelectMaterial = async (materialId) => {
    if (!materialId) {
      toastError(t(n_upcoming_lessons.edit_materials.tab_desc))
      return
    }
    onStartConversation(materialId)
  }

  return (
    <div className="flex w-full mx-[20px] flex-row items-center justify-between">
      <div className="" onClick={() => router.back()}>
        <QIcon_chevron_left className="h-24 w-24 text-black" />
      </div>
      <div className="">
        {isConversationAvailable && (
          <DSButton
            isLoading={loading}
            size={BUTTON_SIZE.SM}
            type={BUTTON_TYPE.SOLID}
            onClick={() => handleSelectMaterial(materialId)}
          >
            <DSButton.Label>{t(n_ai_speaking.material.btn_start)}</DSButton.Label>
          </DSButton>
        )}
      </div>
    </div>
  )
}

const AITutorDiscussionMaterialDetailActivity: ActivityComponentType = ({ params }: IMaterialDetail) => {
  const { useAITutorFlow } = useAITutorStack()
  const { push, pop } = useAITutorFlow()
  const router = useAppRouter({ stackPush: push, browserDetect: true })
  const { currentUser } = useCurrentUser()

  const materialId = router?.query?.materialId || params.materialId

  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const [materialData, setMaterialData] = useState(null)
  const [isConversationAvailable, setIsConversationAvailable] = useState<boolean | { [x: string]: boolean }>(true)

  const getMaterailData = async () => {
    const queryClient = new QueryClient()

    const [_, json] = await Promise.all([
      queryClient.prefetchQuery(['material', materialId], () => fetchMaterial(materialId.toString())),
      axiosGet(apis.course.detail({ id: materialId })),
    ])

    if (json.success) {
      setMaterialData(json)
    }
  }

  useEffect(() => {
    getMaterailData()
  }, [])

  useEffect(() => {
    if (!materialData) return
    if (!currentUser) return
    setIsConversationAvailable(!materialData.course.is_speaking && !materialData.course.is_writing && !!currentUser)
  }, [materialData, currentUser])

  return (
    <TextProvider>
      <WebviewStackContainer
        title={
          <AITutorDiscussionMaterialDetailBar
            pop={pop}
            materialId={materialId}
            isConversationAvailable={isConversationAvailable}
          />
        }
      >
        <MaterialSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />
        {materialData && (
          <div className="relative mt-[20px] h-screen">
            <MaterialBodyComponent activeTabIdx={activeTabIdx} materialData={materialData} materialId={materialId} />
          </div>
        )}
      </WebviewStackContainer>
      {materialData && <MaterialMobileBottomTab course={materialData?.course} />}
    </TextProvider>
  )
}

export default AITutorDiscussionMaterialDetailActivity
